import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { ChevronRight, ArrowLeft, GitBranch } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtR = (n) => `R${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const iso = (d) => d.toISOString().slice(0, 10);

const STAGE_COLORS = {
  applications: 'border-blue-300 bg-blue-50 text-blue-800',
  completed: 'border-sky-300 bg-sky-50 text-sky-800',
  approved: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  paid_out: 'border-teal-300 bg-teal-50 text-teal-800',
  collection_due: 'border-amber-300 bg-amber-50 text-amber-800',
  collected: 'border-green-300 bg-green-50 text-green-800',
  repaid: 'border-emerald-400 bg-emerald-100 text-emerald-900',
  defaulted: 'border-red-300 bg-red-50 text-red-800',
};

export default function PipelinePanel({ getAuthHeader }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('month');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [drill, setDrill] = useState(null); // {key, label}
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(true);

  const range = useCallback(() => {
    const now = new Date();
    if (filter === 'today') { const t = iso(now); return { from: t, to: t }; }
    if (filter === 'week') {
      const d = new Date(now); d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
      return { from: iso(d), to: iso(now) };
    }
    if (filter === 'month') return { from: iso(new Date(now.getFullYear(), now.getMonth(), 2)), to: iso(now) };
    if (filter === 'custom') return { from: custom.from, to: custom.to };
    return { from: '', to: '' };
  }, [filter, custom]);

  const fetchPipeline = useCallback(async () => {
    setLoading(true);
    try {
      const { from, to } = range();
      const res = await axios.get(`${API_URL}/admin/loan-pipeline?date_from=${from}&date_to=${to}`, getAuthHeader());
      setData(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  useEffect(() => { fetchPipeline(); }, [fetchPipeline]);

  const openJourney = async (appId) => {
    if (!appId) return;
    try {
      const res = await axios.get(`${API_URL}/admin/loans/${appId}/journey`, getAuthHeader());
      setJourney(res.data);
    } catch { /* ignore */ }
  };

  return (
    <Card data-testid="pipeline-panel">
      <CardContent className="pt-4 pb-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <p className="text-slate-500 text-xs uppercase font-semibold flex items-center gap-1.5">
            <GitBranch className="w-3.5 h-3.5" /> Loan Lifecycle Pipeline
            <span className="normal-case font-normal text-slate-400">· NuPay/DebiCheck collections only</span>
          </p>
          <div className="flex flex-wrap items-center gap-1">
            {[['today', 'Today'], ['week', 'This Week'], ['month', 'This Month'], ['all', 'All Time'], ['custom', 'Custom']].map(([k, label]) => (
              <Button key={k} variant={filter === k ? 'default' : 'outline'} size="sm" className="h-7 text-xs"
                onClick={() => setFilter(k)} data-testid={`pipeline-filter-${k}`}>{label}</Button>
            ))}
            {filter === 'custom' && (
              <div className="flex items-center gap-1">
                <Input type="date" className="h-7 w-[135px] text-xs" value={custom.from} onChange={e => setCustom(p => ({ ...p, from: e.target.value }))} data-testid="pipeline-custom-from" />
                <span className="text-xs text-slate-400">to</span>
                <Input type="date" className="h-7 w-[135px] text-xs" value={custom.to} onChange={e => setCustom(p => ({ ...p, to: e.target.value }))} data-testid="pipeline-custom-to" />
              </div>
            )}
          </div>
        </div>

        {loading && !data ? (
          <div className="flex justify-center py-8"><div className="spinner"></div></div>
        ) : (
          <div className="flex flex-wrap items-stretch gap-y-3">
            {(data?.stages || []).map((s, i) => (
              <div key={s.key} className="flex items-center">
                <button onClick={() => setDrill(s)} data-testid={`pipeline-stage-${s.key}`}
                  className={`rounded-xl border-2 px-3 py-2.5 text-left hover:shadow-md transition-shadow min-w-[128px] ${STAGE_COLORS[s.key]}`}>
                  <p className="text-[10px] font-bold uppercase leading-tight opacity-80">{s.label}</p>
                  <p className="text-xl font-bold tabular-nums leading-tight mt-0.5">{s.count}</p>
                  <p className="text-[11px] font-semibold tabular-nums opacity-90">{fmtR(s.value)}</p>
                </button>
                {i < data.stages.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300 mx-0.5 shrink-0" />}
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-slate-400 mt-3">Click any stage to see the clients behind the number. Click a client to follow their loan from application to final repayment.</p>
      </CardContent>

      {/* Drill-down dialog */}
      <Dialog open={!!drill} onOpenChange={(o) => { if (!o) { setDrill(null); setJourney(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {journey ? (
            <div data-testid="loan-journey">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setJourney(null)} data-testid="journey-back-btn">
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  Loan Journey — {journey.application.client} (#{journey.application.reference})
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 my-3">
                {[['Requested', journey.application.loan_amount], ['Approved', journey.application.approved_amount],
                  ['Collected', journey.collection_summary.total_collected], ['Outstanding', journey.collection_summary.outstanding]].map(([l, v]) => (
                  <div key={l} className="bg-slate-50 rounded-lg p-2.5">
                    <p className="text-[10px] text-slate-500 uppercase font-semibold">{l}</p>
                    <p className="text-sm font-bold tabular-nums">{v == null ? '—' : fmtR(v)}</p>
                  </div>
                ))}
              </div>
              <div className="space-y-0">
                {journey.events.map((e, i) => (
                  <div key={i} className="flex gap-3" data-testid={`journey-event-${i}`}>
                    <div className="flex flex-col items-center">
                      <span className={`w-2.5 h-2.5 rounded-full mt-1 shrink-0 ${
                        e.type === 'application' ? 'bg-blue-500' :
                        e.type === 'status' ? 'bg-purple-500' :
                        e.type === 'payment' ? 'bg-emerald-500' :
                        e.title.includes('FAILED') || e.title.includes('REVERSED') ? 'bg-red-500' :
                        e.title.includes('Collected') ? 'bg-green-500' : 'bg-amber-500'}`}></span>
                      {i < journey.events.length - 1 && <span className="w-px flex-1 bg-slate-200"></span>}
                    </div>
                    <div className="pb-4">
                      <p className="text-xs text-slate-400">{e.date || '—'}</p>
                      <p className="text-sm font-semibold text-slate-800">{e.title}</p>
                      {e.detail && <p className="text-xs text-slate-500">{e.detail}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>{drill?.label} — {drill?.count} · {fmtR(drill?.value)}</DialogTitle>
              </DialogHeader>
              <div className="overflow-x-auto">
                <table className="data-table" data-testid="pipeline-drill-table">
                  <thead><tr><th>Client</th><th>Reference</th><th>Amount</th><th>Date</th><th>Status</th><th></th></tr></thead>
                  <tbody>
                    {(data?.rows?.[drill?.key] || []).map((r, i) => (
                      <tr key={i} className={r.app_id ? 'cursor-pointer hover:bg-slate-50' : ''}
                        onClick={() => openJourney(r.app_id)} data-testid={`pipeline-row-${i}`}>
                        <td className="font-medium">{r.client}</td>
                        <td className="font-mono text-xs">{r.reference || '—'}</td>
                        <td className="tabular-nums">{fmtR(r.amount)}</td>
                        <td className="text-xs text-slate-500 whitespace-nowrap">{r.date || '—'}</td>
                        <td><Badge variant="outline" className="text-[10px]">{r.status || r.extra || '—'}</Badge></td>
                        <td>{r.app_id && <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}</td>
                      </tr>
                    ))}
                    {(data?.rows?.[drill?.key] || []).length === 0 && (
                      <tr><td colSpan="6" className="text-center text-sm text-slate-400 py-6">Nothing in this stage for the selected period</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
